import { useState, useEffect, useMemo } from "react";
import {
  Input,
  Button,
  Stack,
  FormControl,
  FormLabel,
  Switch,
  Textarea,
  HStack,
  Box,
  Image,
  Text,
  Progress,
  Flex,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useJsApiLoader } from "@react-google-maps/api";

import AutocompleteInput from "../../../LocationsModule/PlaceAutocompleteInput"; // ✅ same as SearchTrips
import { uploadListingImagesToFirebase } from "../Accomodations/uploadListingImagesToFirebase";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";
import { CAR_CATEGORIES } from "./car.types";

interface CarFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
}

const MAX_FILES = 12;
const cardsBorderRadii = "8px";

const CarForm: React.FC<CarFormProps> = ({ initialData, onSubmit }) => {
  const { theme } = useCustomTheme();
  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  // ✅ Load Google Places
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });

  const [formData, setFormData] = useState({
    make: "",
    model: "",
    brand: "",
    category: "SUV",
    year: "",
    pricePerDay: "",
    seats: "",
    transmission: "",
    availability: true,
    pickupLocation: "",
    description: "",
    images: [] as string[],
    imageUrl: "",
  });

  // ✅ store coords (backward compatible)
  const [pickupCoords, setPickupCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // ✅ FIX 1: Places dropdown hidden behind Chakra Modal/Drawer
  // Google renders suggestions inside `.pac-container` appended to <body>.
  // Chakra modal overlay has high z-index → suggestions appear behind it.
  // This forces suggestions ABOVE modals.
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-pac-zindex-fix", "true");
    style.innerHTML = `
      .pac-container {
        z-index: 20000 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);

  useEffect(() => {
    if (!initialData) return;

    const imgs =
      Array.isArray(initialData.images) && initialData.images.length
        ? initialData.images
        : initialData.imageUrl
          ? [initialData.imageUrl]
          : [];

    setFormData({
      make: initialData.make ?? "",
      model: initialData.model ?? "",
      brand: initialData.brand ?? "",
      category: CAR_CATEGORIES.includes(initialData.category as any)
        ? initialData.category
        : "Other",
      year: initialData.year ?? "",
      pricePerDay: initialData.pricePerDay ?? "",
      seats: initialData.seats ?? "",
      transmission: initialData.transmission ?? "",
      availability: initialData.availability ?? true,
      pickupLocation: initialData.pickupLocation ?? "",
      description: initialData.description ?? "",
      images: imgs,
      imageUrl: initialData.imageUrl ?? "",
    });

    // ✅ hydrate coords if saved
    const lat =
      initialData.pickupLat ??
      initialData.pickupLocationLat ??
      initialData.pickupLocation?.lat;
    const lng =
      initialData.pickupLng ??
      initialData.pickupLocationLng ??
      initialData.pickupLocation?.lng;

    if (typeof lat === "number" && typeof lng === "number") {
      setPickupCoords({ lat, lng });
    } else {
      setPickupCoords(null);
    }

    setExistingImages(imgs);
    setFiles([]);
    setUploading(false);
    setUploadProgress(0);
  }, [initialData]);

  useEffect(() => {
    if (!initialData) {
      setExistingImages(Array.isArray(formData.images) ? formData.images : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newPreviewUrls = useMemo(
    () => files.map((f) => URL.createObjectURL(f)),
    [files],
  );
  const previewList = useMemo(
    () => [...existingImages, ...newPreviewUrls],
    [existingImages, newPreviewUrls],
  );

  const inputStyles = {
    borderColor: theme.inputBorder,
    bg: theme.inputBg,
    color: theme.text,
    _placeholder: { color: theme.inputPlaceholder },
    _hover: { borderColor: theme.inputBorderFocus },
    _focus: {
      borderColor: theme.inputBorderFocus,
      boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
    },
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAvailabilityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, availability: e.target.checked });
  };

  const handlePickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    setFiles((prev) => {
      const merged = [...prev, ...picked];
      return merged.slice(0, MAX_FILES);
    });

    e.target.value = "";
  };

  const removeAt = (index: number) => {
    if (index < existingImages.length) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    const localIdx = index - existingImages.length;
    setFiles((prev) => prev.filter((_, i) => i !== localIdx));
  };

  const handleSubmit = async () => {
    const userId = localStorage.getItem("userId") || "guest";
    const key = initialData?._id || `${userId}-${Date.now()}`;

    try {
      setUploading(true);
      setUploadProgress(15);

      let imageUrls: string[] = existingImages || [];

      if (files.length > 0) {
        setUploadProgress(35);
        const uploaded = await uploadListingImagesToFirebase(
          files,
          key,
          "cars",
        );
        imageUrls = [...imageUrls, ...uploaded];
      }

      setUploadProgress(90);

      const finalImages = Array.isArray(imageUrls) ? imageUrls : [];

      onSubmit({
        ...formData,
        userId,

        images: finalImages,
        imageUrl: finalImages[0] || formData.imageUrl || "",

        // ✅ add coords
        pickupLocationCoords: pickupCoords
          ? { lat: pickupCoords.lat, lng: pickupCoords.lng }
          : undefined,
      });

      setUploadProgress(100);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 250);
    }
  };

  return (
    <Stack spacing={3}>
      <Input
        name="make"
        placeholder="Make"
        value={formData.make}
        onChange={handleTextChange}
        {...inputStyles}
      />
      <Input
        name="model"
        placeholder="Model"
        value={formData.model}
        onChange={handleTextChange}
        {...inputStyles}
      />
      <FormControl isRequired>
        <FormLabel color={theme.text}>Category</FormLabel>

        <Box w="100%" minW={0} position="relative">
          <Menu matchWidth placement="bottom-start" gutter={4}>
            {({ isOpen }) => (
              <>
                <MenuButton
                  as={Button}
                  w="100%"
                  h="40px"
                  minW={0}
                  px={3}
                  bg={theme.inputBg}
                  color={theme.text}
                  border="1px solid"
                  borderColor={
                    isOpen ? theme.inputBorderFocus : theme.inputBorder
                  }
                  borderRadius={cardsBorderRadii}
                  fontWeight="400"
                  fontSize="md"
                  textAlign="left"
                  boxShadow={
                    isOpen ? `0 0 0 1px ${theme.inputBorderFocus}` : "none"
                  }
                  rightIcon={
                    <ChevronDownIcon
                      boxSize={4}
                      transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                      transition="transform 0.18s ease"
                    />
                  }
                  _hover={{
                    bg: theme.inputBg,
                    borderColor: theme.inputBorderFocus,
                  }}
                  _active={{ bg: theme.inputBg }}
                  _focusVisible={{
                    borderColor: theme.inputBorderFocus,
                    boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                  }}
                  sx={{
                    "& > span:first-of-type": {
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  {formData.category}
                </MenuButton>

                <MenuList
                  w="100%"
                  minW="100%"
                  maxW="100%"
                  p={1}
                  maxH="250px"
                  overflowY="auto"
                  overflowX="hidden"
                  bg={theme.surface}
                  color={theme.text}
                  border="1px solid"
                  borderColor={theme.borderStrong}
                  borderRadius={cardsBorderRadii}
                  boxShadow={theme.shadowMd}
                  zIndex={30}
                  sx={{
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: theme.borderStrong,
                      borderRadius: {cardsBorderRadii}
                    },
                  }}
                >
                  {CAR_CATEGORIES.map((category) => {
                    const selected = formData.category === category;

                    return (
                      <MenuItem
                        key={category}
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, category }))
                        }
                        w="100%"
                        minW={0}
                        px={3}
                        py={2}
                        borderRadius={cardsBorderRadii}
                        bg={selected ? theme.surfaceAlt : "transparent"}
                        color={selected ? theme.primary : theme.text}
                        fontWeight={selected ? "700" : "600"}
                        fontSize="sm"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        _hover={{
                          bg: theme.hover,
                          color: theme.text,
                        }}
                        _focus={{
                          bg: theme.hover,
                          color: theme.text,
                        }}
                      >
                        {category}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </>
            )}
          </Menu>
        </Box>

        <Text mt={1} fontSize="xs" color={theme.textSubtle}>
          Category is required and is used by customers to filter car-hire
          listings.
        </Text>
      </FormControl>
      <Input
        name="year"
        placeholder="Year"
        type="number"
        value={formData.year}
        onChange={handleTextChange}
        {...inputStyles}
      />
      <Input
        name="pricePerDay"
        placeholder="Price Per Day"
        type="number"
        value={formData.pricePerDay}
        onChange={handleTextChange}
        {...inputStyles}
      />
      <Input
        name="seats"
        placeholder="Seats"
        type="number"
        value={formData.seats}
        onChange={handleTextChange}
        {...inputStyles}
      />
      <Input
        name="transmission"
        placeholder="Transmission"
        value={formData.transmission}
        onChange={handleTextChange}
        {...inputStyles}
      />
      <Input
        name="brand"
        placeholder="brand"
        value={formData.brand}
        onChange={handleTextChange}
        {...inputStyles}
      />

      <FormControl display="flex" alignItems="center">
        <FormLabel htmlFor="availability" mb="0" color={theme.text}>
          Available
        </FormLabel>
        <Switch
          id="availability"
          name="availability"
          isChecked={formData.availability}
          onChange={handleAvailabilityChange}
        />
      </FormControl>

      {/* ✅ Pickup location with Google Places */}
      <FormControl>
        <FormLabel color={theme.text}>Pickup Location</FormLabel>

        {/* If key missing, use manual input */}
        {!API_KEY ? (
          <Input
            name="pickupLocation"
            placeholder="pickupLocation"
            value={formData.pickupLocation}
            onChange={handleTextChange}
            {...inputStyles}
          />
        ) : loadError ? (
          // If maps failed, show message + manual input
          <Box>
            <Text fontSize="sm" color={theme.danger} mb={2}>
              Maps failed to load. Using manual input.
            </Text>
            <Input
              name="pickupLocation"
              placeholder="pickupLocation"
              value={formData.pickupLocation}
              onChange={handleTextChange}
              {...inputStyles}
            />
          </Box>
        ) : !isLoaded ? (
          // If maps still loading, show spinner (instead of silently falling back)
          <Flex align="center" gap={2}>
            <GlobalSpinner />
            <Text fontSize="sm" color={theme.textSubtle}>
              Loading location search…
            </Text>
          </Flex>
        ) : (
          // ✅ Normal working Autocomplete
          <AutocompleteInput
            placeholder="Pickup location"
            value={formData.pickupLocation}
            onPlaceSelected={(addr: string) =>
              setFormData((p) => ({ ...p, pickupLocation: addr }))
            }
            onLocationSelected={(coords) => setPickupCoords(coords)}
          />
        )}

        {pickupCoords && (
          <Text fontSize="xs" color={theme.textSubtle} mt={1}>
            Lat: {pickupCoords.lat.toFixed(5)} • Lng:{" "}
            {pickupCoords.lng.toFixed(5)}
          </Text>
        )}
      </FormControl>

      <FormControl>
        <FormLabel color={theme.text}>Description (optional)</FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          placeholder="Short description..."
          borderColor={theme.inputBorder}
          bg={theme.inputBg}
          color={theme.text}
          _placeholder={{ color: theme.inputPlaceholder }}
          _hover={{ borderColor: theme.inputBorderFocus }}
          _focus={{
            borderColor: theme.inputBorderFocus,
            boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
          }}
        />
      </FormControl>

      {/* ✅ MULTIPLE IMAGE UPLOAD (append) */}
      <FormControl>
        <FormLabel color={theme.text}>Car Images (multiple)</FormLabel>
        <Input
          type="file"
          accept="image/*"
          multiple
          onChange={handlePickFiles}
          {...inputStyles}
        />

        {uploading && (
          <Box mt={2}>
            <Text fontSize="sm" mb={1} color={theme.primary}>
              Uploading...
            </Text>
            <Progress
              value={uploadProgress}
              size="sm"
              borderRadius={cardsBorderRadii}
              isIndeterminate={uploadProgress === 0}
            />
          </Box>
        )}

        <Text fontSize="sm" mt={2} color={theme.textSubtle}>
          Upload 1 image now and add more later. New images will be uploaded and
          appended to the existing list.
        </Text>

        {previewList.length > 0 && (
          <HStack mt={3} spacing={2} overflowX="auto" py={1}>
            {previewList.map((src, idx) => (
              <Box
                key={`${src}-${idx}`}
                position="relative"
                borderWidth="1px"
                borderColor={theme.border}
                borderRadius={cardsBorderRadii}
                overflow="hidden"
                flex="0 0 auto"
                bg={theme.surface}
              >
                <Image
                  src={src}
                  alt={`car-img-${idx}`}
                  boxSize="72px"
                  objectFit="cover"
                />
                <Button
                  type="button"
                  size="xs"
                  colorScheme="red"
                  position="absolute"
                  top="-8px"
                  right="-8px"
                  borderRadius={cardsBorderRadii}
                  onClick={() => removeAt(idx)}
                >
                  ×
                </Button>
              </Box>
            ))}
          </HStack>
        )}
      </FormControl>

      <Flex justify="flex-end">
        <Button
          onClick={handleSubmit}
          isLoading={uploading}
          color={theme.textOnPrimary}
          bgImage={theme.gradients.primaryButton}
          _hover={{ bgImage: theme.gradients.primaryButtonHover }}
          boxShadow={theme.shadowSm}
        >
          {initialData ? "Update Car" : "Submit"}
        </Button>
      </Flex>
    </Stack>
  );
};

export default CarForm;
